import { generatePath } from 'react-router';

import { VIEW_PATHS_MARKET_INSIGHTS } from '@hcs/app-configs';
import { CircleButton, DownloadSaveIcon, Popover } from '@hcs/design-system';
import { MeaningfulEventTypes } from '@hcs/types';
import { openInNewTab } from '@hcs/utils';
import { useGetXLS } from '@hcs/xls-service';

import {
  useCerberusStatsFields,
  useCityXLSData,
  useMSAXLSData,
  useZipXLSData,
} from '../../hooks';

import { CityGeoConfig, MsaGeoConfig, ZipGeoConfig } from './GeoQuickActions';

import styles from './GeoQuickActions.module.css';

const dataHcName = 'geo-quick-actions';
const dataHcEventSection = 'Download Report';

// Calculate fromDate as 20 years ago to limit HPI/RPI historical data range
const FROM_DATE = ((): `${number}-${number}-${number}` => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 20);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return `${year}-${month}-${day}` as `${number}-${number}-${number}`;
})();

export const GeoDownloadReport = ({
  geoConfig,
}: {
  geoConfig: ZipGeoConfig | MsaGeoConfig | CityGeoConfig;
}) => {
  const { data: fieldsData, isLoading: fieldsDataIsLoading } =
    useCerberusStatsFields(
      geoConfig.type === 'msa' ? 'chartsMsa' : 'chartsZip',
    );
  const { mutate: generateXLS } = useGetXLS();

  // Fetch XLS data based on geo type (hooks will skip when IDs are empty)
  const msaId = geoConfig.type === 'msa' ? geoConfig.msaId : '';
  const zipcode = geoConfig.type === 'zip' ? geoConfig.zipcode : '';
  const placeId = geoConfig.type === 'city' ? geoConfig.placeId : '';
  const propertyType = geoConfig.propertyType;
  const {
    xlsOperations: msaXlsOperations,
    isLoading: msaIsLoading,
    isReady: msaIsReady,
  } = useMSAXLSData({
    msaId,
    fromDate: FROM_DATE,
    marketActivityFields: fieldsData?.order,
    propertyType,
  });

  const {
    xlsOperations: zipXlsOperations,
    isLoading: zipIsLoading,
    isReady: zipIsReady,
  } = useZipXLSData({
    zipcode,
    fromDate: FROM_DATE,
    marketActivityFields: fieldsData?.order,
    propertyType,
  });

  const {
    xlsOperations: cityXlsOperations,
    isLoading: cityIsLoading,
    isReady: cityIsReady,
  } = useCityXLSData({
    placeId,
    fromDate: FROM_DATE,
    marketActivityFields: fieldsData?.order,
    propertyType,
  });

  // Get the active XLS operations and loading state based on geo type
  const xlsOperations =
    geoConfig.type === 'msa'
      ? msaXlsOperations
      : geoConfig.type === 'zip'
        ? zipXlsOperations
        : cityXlsOperations;

  const xlsDataIsLoading =
    geoConfig.type === 'msa'
      ? msaIsLoading
      : geoConfig.type === 'zip'
        ? zipIsLoading
        : cityIsLoading;

  const xlsDataIsReady =
    geoConfig.type === 'msa'
      ? msaIsReady
      : geoConfig.type === 'zip'
        ? zipIsReady
        : cityIsReady;

  let geoId: string | null = null;
  if (geoConfig.type === 'msa') {
    geoId = geoConfig.msaId;
  } else if (geoConfig.type === 'zip') {
    geoId = geoConfig.zipcode;
  } else if (geoConfig.type === 'city') {
    geoId = geoConfig.placeId;
  }
  const handleDownloadReportPdf = () => {
    const ptParam = `?propertyType=${geoConfig.propertyType}`;
    if (geoConfig.type === 'msa') {
      openInNewTab(
        `${VIEW_PATHS_MARKET_INSIGHTS.PDFS}/${generatePath(
          `${VIEW_PATHS_MARKET_INSIGHTS.MSA_REPORT}`,
          { msaId: geoId },
        )}${ptParam}`,
      );
    } else if (geoConfig.type === 'city') {
      openInNewTab(
        `${VIEW_PATHS_MARKET_INSIGHTS.PDFS}/${generatePath(
          `${VIEW_PATHS_MARKET_INSIGHTS.CITY_REPORT}`,
          { placeId: geoId },
        )}${ptParam}`,
      );
    } else {
      openInNewTab(
        `${VIEW_PATHS_MARKET_INSIGHTS.PDFS}/${generatePath(
          `${VIEW_PATHS_MARKET_INSIGHTS.ZIP_REPORT}`,
          { zipcode: geoId },
        )}${ptParam}`,
      );
    }
  };
  const handleDownloadReportExcel = () => {
    if (!xlsOperations || !xlsDataIsReady) {
      console.warn('XLS data not ready yet', {
        xlsOperations,
        xlsDataIsReady,
        xlsDataIsLoading,
      });
      return;
    }

    const ptSuffix = propertyType === 'condo' ? ' condo' : ' sfr';
    const fileName = `${geoId}_MarketInsights${ptSuffix}.xlsx`;

    generateXLS({
      fileName,
      xlsJsonData: xlsOperations,
    });
  };
  return (
    <Popover
      dataHcName={`${dataHcName}-popover`}
      dataHcEventSection={dataHcEventSection}
      closeOnClick
      trigger={
        <CircleButton
          disabled={fieldsDataIsLoading || xlsDataIsLoading || !xlsDataIsReady}
          dataHcName={`${dataHcName}-download-report-button`}
          dataHcEventType={MeaningfulEventTypes.Goal}
          dataHcEventName={`MI Download ${geoConfig.type} report`}
          size="lg"
          label="Download"
          buttonStyle="tertiary"
          shadow={false}
        >
          <DownloadSaveIcon
            dataHcName={`${dataHcName}-download-report-button-icon`}
            dataHcEventIgnore
            size="lg"
          />
        </CircleButton>
      }
      content={
        <div data-hc-name={`${dataHcName}-content`} className={styles.Content}>
          <ul className={styles.Buttons}>
            <li
              onClick={handleDownloadReportPdf}
              className={styles.Button}
              data-hc-name={`${dataHcName}-download-pdf-button`}
              data-hc-event-name={'Download Report PDF'}
              data-hc-event-type={MeaningfulEventTypes.Goal}
            >
              Print as PDF
            </li>
            <li
              onClick={handleDownloadReportExcel}
              className={styles.Button}
              data-hc-name={`${dataHcName}-download-excel-button`}
              data-hc-event-name={'Download Report Spreadsheet'}
              data-hc-event-type={MeaningfulEventTypes.Goal}
            >
              Excel
            </li>
          </ul>
        </div>
      }
    />
  );
};
